import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  value: string;
  onSave: (next: string) => Promise<unknown> | void;
  /** When true, mark the cell as RTL — for Arabic values. */
  rtl?: boolean;
}

/**
 * Single-cell inline editor. Displays the value as text; on click,
 * swaps to a textarea anchored to the cell. Save on blur or Enter,
 * cancel on Escape. Empty values are rejected client-side — the API
 * also rejects them with a 400.
 */
export default function EditableCell({ value, onSave, rtl }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing) {
      setDraft(value);
      // Defer focus so React mounts the textarea first.
      requestAnimationFrame(() => ref.current?.focus());
    }
  }, [editing, value]);

  const commit = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setEditing(false);
      setDraft(value);
      return;
    }
    if (trimmed === value) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(trimmed);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };

  if (editing) {
    return (
      <div>
        <textarea
          ref={ref}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void commit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
          disabled={busy}
          dir={rtl ? 'rtl' : 'ltr'}
          rows={Math.min(4, Math.max(1, Math.ceil(draft.length / 60)))}
          style={{
            width: '100%',
            minHeight: 28,
            padding: '4px 6px',
            border: '1px solid var(--ds-color-primary, #1677ff)',
            borderRadius: 4,
            font: 'inherit',
            resize: 'vertical',
            background: '#fff',
          }}
        />
        {busy && (
          <div className="ds-muted" style={{ fontSize: 11, marginTop: 4 }}>
            {t('translations.row.saving')}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setEditing(true);
        }
      }}
      dir={rtl ? 'rtl' : 'ltr'}
      title={t('translations.row.editHint')}
      style={{
        cursor: 'text',
        padding: '4px 6px',
        borderRadius: 4,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        minHeight: 24,
      }}
    >
      {value || <span style={{ color: '#aaa', fontStyle: 'italic' }}>—</span>}
    </div>
  );
}
